import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { platformIamService } from '../services/api';
import { canExecutePlatformActionWithContext } from '../utils/platformPermissions';

const permissionCache = new Map();
const permissionInFlight = new Map();

const toPermissionSnapshot = (payload) => ({
  permissions: Array.isArray(payload?.permissions)
    ? payload.permissions
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    : [],
  usingLegacyFallback: payload?.using_legacy_fallback === true,
});

const getCacheKey = (user) => {
  const fromId = String(user?.id || '').trim();
  if (fromId) return `id:${fromId}`;

  const fromPlatformUserId = String(user?.platform_user_id || '').trim();
  if (fromPlatformUserId) return `platform:${fromPlatformUserId}`;

  const fromEmail = String(user?.email || '').trim().toLowerCase();
  if (fromEmail) return `email:${fromEmail}`;

  return 'anonymous-platform-admin';
};

const getPermissionsCached = async (cacheKey) => {
  if (permissionCache.has(cacheKey)) {
    return permissionCache.get(cacheKey);
  }

  if (permissionInFlight.has(cacheKey)) {
    return permissionInFlight.get(cacheKey);
  }

  const request = platformIamService
    .getIamMePermissions()
    .then((response) => {
      const snapshot = toPermissionSnapshot(response?.data || {});
      permissionCache.set(cacheKey, snapshot);
      return snapshot;
    })
    .finally(() => {
      permissionInFlight.delete(cacheKey);
    });

  permissionInFlight.set(cacheKey, request);
  return request;
};

export const usePlatformActionPermissions = () => {
  const { user } = useAuth();
  const isPlatformAdmin = user?.is_platform_admin === true;

  const [permissionState, setPermissionState] = React.useState({
    loading: false,
    error: false,
    permissionsLoaded: false,
    usingLegacyFallback: false,
    permissions: [],
  });

  React.useEffect(() => {
    if (!isPlatformAdmin) {
      setPermissionState({
        loading: false,
        error: false,
        permissionsLoaded: false,
        usingLegacyFallback: false,
        permissions: [],
      });
      return;
    }

    let cancelled = false;
    const cacheKey = getCacheKey(user);

    setPermissionState((previous) => ({
      ...previous,
      loading: true,
      error: false,
    }));

    getPermissionsCached(cacheKey)
      .then((snapshot) => {
        if (cancelled) return;
        setPermissionState({
          loading: false,
          error: false,
          permissionsLoaded: true,
          usingLegacyFallback: snapshot.usingLegacyFallback,
          permissions: snapshot.permissions,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setPermissionState({
          loading: false,
          error: true,
          permissionsLoaded: false,
          usingLegacyFallback: false,
          permissions: [],
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin, user]);

  const canExecuteAction = React.useCallback(
    (actionKey) =>
      canExecutePlatformActionWithContext({
        actionKey,
        userPermissions: permissionState.permissions,
        isPlatformAdmin,
        usingLegacyFallback: permissionState.usingLegacyFallback,
        permissionsLoaded: permissionState.permissionsLoaded,
        hasPermissionLoadError: permissionState.error,
      }),
    [isPlatformAdmin, permissionState]
  );

  return {
    canExecuteAction,
    isPlatformAdmin,
    permissionState,
  };
};

export default usePlatformActionPermissions;
